import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtCurrency, formatApiError, mutateOrQueue, getPartsResilient, getPartByNumberResilient } from "@/lib/api";
import { toast } from "sonner";
import { Search, Trash2, ClipboardList, ScanLine } from "lucide-react";
import BarcodeInput from "@/components/BarcodeInput";

export default function Billing() {
  const [parts, setParts] = useState([]);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [customerName, setCustomerName] = useState("Walk-in Customer");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    getPartsResilient(q ? { q } : {}).then(({ data }) => setParts(data));
  }, [q]);

  const add = (p) => {
    if (p.stock <= 0) return toast.error("Out of stock");
    if (items.find((i) => i.part.id === p.id)) {
      setItems(items.map((i) => i.part.id === p.id ? { ...i, qty: Math.min(i.qty + 1, p.stock) } : i));
    } else {
      setItems([...items, { part: p, qty: 1 }]);
    }
  };
  const setQty = (id, qty) => setItems(items.map((i) => i.part.id === id ? { ...i, qty: Math.max(1, Math.min(Number(qty) || 1, i.part.stock)) } : i));
  const remove = (id) => setItems(items.filter((i) => i.part.id !== id));
  const total = items.reduce((s, i) => s + i.qty * (i.part.mrp ?? i.part.retail_price ?? 0), 0);

  const handleScan = async (code) => {
    try {
      const part = await getPartByNumberResilient(code);
      add(part);
      toast.success(`+1 ${part.part_number}`);
    } catch (err) {
      if (err?.response?.status === 404) toast.error(`No part with barcode ${code}`);
      else toast.error(formatApiError(err));
    }
  };

  const submit = async () => {
    if (items.length === 0) return toast.error("Add parts to bill");
    setBusy(true);
    try {
      const res = await mutateOrQueue({
        method: "post",
        url: "/invoices/direct",
        body: {
          items: items.map((i) => ({ part_id: i.part.id, qty: i.qty })),
          customer_name: customerName || "Walk-in Customer",
        },
        label: `Invoice · ${customerName || "Walk-in"} · ${fmtCurrency(total)}`,
      });
      if (res.queued) {
        toast.success("You're offline — invoice queued and will sync when you reconnect");
        nav("/sync");
      } else {
        toast.success(`Invoice ${res.data.number} created`);
        nav(`/invoices/${res.data.id}`);
      }
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="billing-root">
      <div className="flex items-end justify-between border-b border-zinc-200 pb-6">
        <div>
          <div className="label-eyebrow">Counter</div>
          <h1 className="text-4xl font-bold tracking-tight text-zinc-950">Billing Counter</h1>
          <p className="text-sm text-zinc-500 mt-2">Search parts and generate an invoice instantly. Inventory is decremented automatically.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
        <div className="lg:col-span-2 bg-white">
          <div className="p-4 border-b border-zinc-200 space-y-3">
            <div>
              <div className="label-eyebrow mb-1.5 flex items-center gap-2"><ScanLine className="w-3 h-3 text-[#002FA7]" />Barcode scan</div>
              <BarcodeInput
                testid="billing-barcode-input"
                placeholder="Scan a part to add to cart…"
                hint="Scanner is HID-keyboard mode — focus stays here for rapid scans"
                onScan={handleScan}
                autoFocus
              />
            </div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                data-testid="billing-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Or search parts manually..."
                className="w-full bg-white border border-zinc-300 text-sm pl-10 pr-3 py-2.5 font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              />
            </div>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50 text-zinc-500 uppercase tracking-wider text-[11px] border-b border-zinc-200">
                  <th className="text-left px-5 py-2 font-semibold">Part #</th>
                  <th className="text-left px-5 py-2 font-semibold">Description</th>
                  <th className="text-right px-5 py-2 font-semibold">Stock</th>
                  <th className="text-right px-5 py-2 font-semibold">Price</th>
                  <th className="text-right px-5 py-2 font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {parts.map((p) => (
                  <tr key={p.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                    <td className="px-5 py-2 font-mono text-zinc-950">{p.part_number}</td>
                    <td className="px-5 py-2 text-zinc-700">{p.manufacturer} · {p.model} {p.year}</td>
                    <td className={`px-5 py-2 text-right font-mono ${p.stock <= 5 ? "text-rose-600" : "text-zinc-700"}`}>{p.stock}</td>
                    <td className="px-5 py-2 text-right font-mono font-semibold">{fmtCurrency((p.mrp ?? p.retail_price ?? 0))}</td>
                    <td className="px-5 py-2 text-right">
                      <button data-testid={`bill-add-${p.part_number}`} disabled={p.stock <= 0} onClick={() => add(p)} className="text-xs uppercase tracking-wider px-2 py-1 border border-zinc-300 hover:bg-zinc-100 disabled:opacity-40">Add</button>
                    </td>
                  </tr>
                ))}
                {parts.length === 0 && (
                  <tr><td colSpan={5} className="px-5 py-12 text-center text-zinc-500 text-sm">No parts found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white flex flex-col">
          <div className="p-4 border-b border-zinc-200">
            <div className="label-eyebrow">Customer</div>
            <input data-testid="billing-customer-input" value={customerName} onChange={(e) => setCustomerName(e.target.value)} className="mt-2 w-full bg-white border border-zinc-300 text-sm px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          </div>
          <div className="flex-1 overflow-y-auto">
            {items.length === 0 && (
              <div className="p-8 text-center text-sm text-zinc-500"><ClipboardList className="w-6 h-6 mx-auto mb-2 text-zinc-300" />No items</div>
            )}
            {items.map((i) => (
              <div key={i.part.id} className="p-3 border-b border-zinc-100" data-testid={`bill-line-${i.part.part_number}`}>
                <div className="flex justify-between items-start mb-2">
                  <div className="min-w-0">
                    <div className="font-mono text-sm font-medium truncate">{i.part.part_number}</div>
                    <div className="text-xs text-zinc-500">{i.part.manufacturer}</div>
                  </div>
                  <button onClick={() => remove(i.part.id)} className="text-rose-600 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                <div className="flex justify-between items-center">
                  <input data-testid={`bill-qty-${i.part.part_number}`} type="number" min="1" max={i.part.stock} value={i.qty} onChange={(e) => setQty(i.part.id, e.target.value)} className="w-20 border border-zinc-300 px-2 py-1 font-mono text-sm" />
                  <div className="font-mono font-semibold">{fmtCurrency(i.qty * (i.part.mrp ?? i.part.retail_price ?? 0))}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-zinc-200 p-4 space-y-3">
            <div className="flex justify-between items-end">
              <div className="label-eyebrow">Total</div>
              <div className="font-mono text-3xl font-bold text-zinc-950" data-testid="bill-total">{fmtCurrency(total)}</div>
            </div>
            <button
              data-testid="bill-create-invoice-button"
              disabled={busy || items.length === 0}
              onClick={submit}
              className="w-full bg-[#002FA7] text-white text-sm uppercase tracking-wider font-semibold py-3 hover:bg-blue-900 disabled:opacity-50"
            >
              {busy ? "Generating..." : "Generate Invoice"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
